import * as THREE from 'three';
import { BIOME_NAMES, BIOME_PROFILES, biomeWeightsAt, type BiomeWeights } from '../world/biomes';

export interface AtmosphereController {
  update(x: number, z: number, delta?: number, snap?: boolean): void;
  dispose(): void;
}

export interface AtmosphereTarget {
  readonly sky: THREE.Color;
  readonly fog: THREE.Color;
  readonly sun: THREE.Color;
  readonly hemisphereSky: THREE.Color;
  readonly hemisphereGround: THREE.Color;
  readonly fogNear: number;
  readonly fogFar: number;
}

function blendColor(weights: BiomeWeights, key: 'skyColor' | 'fogColor' | 'sunColor'): THREE.Color {
  const color = new THREE.Color(0, 0, 0);
  for (const name of BIOME_NAMES) {
    const source = BIOME_PROFILES[name][key];
    color.r += source[0] * weights[name];
    color.g += source[1] * weights[name];
    color.b += source[2] * weights[name];
  }
  return color;
}

export function atmosphereTarget(weights: BiomeWeights): AtmosphereTarget {
  const sky = blendColor(weights, 'skyColor');
  const fog = blendColor(weights, 'fogColor');
  const sun = blendColor(weights, 'sunColor');
  const hemisphereSky = sky.clone().lerp(new THREE.Color(0xf3fbff), 0.48);
  const hemisphereGround = fog.clone().multiplyScalar(0.53);
  let fogNear = 0;
  let fogFar = 0;
  for (const name of BIOME_NAMES) {
    fogNear += BIOME_PROFILES[name].fogNear * weights[name];
    fogFar += BIOME_PROFILES[name].fogFar * weights[name];
  }
  return { sky, fog, sun, hemisphereSky, hemisphereGround, fogNear, fogFar };
}

export function addAtmosphere(scene: THREE.Scene): AtmosphereController {
  // Snow haze closes the horizon naturally, without scenery that could imply
  // a boundary to the otherwise open left/right traversal.
  const weights = biomeWeightsAt(0, -1_200);
  const initial = atmosphereTarget(weights);
  const background = initial.sky.clone();
  const fog = new THREE.Fog(initial.fog, initial.fogNear, initial.fogFar);
  scene.background = background;
  scene.fog = fog;

  const hemisphere = new THREE.HemisphereLight(initial.hemisphereSky, initial.hemisphereGround, 1.42);
  const sun = new THREE.DirectionalLight(initial.sun, 2.85);
  sun.position.set(-110, 145, -90);
  sun.castShadow = true;
  sun.shadow.mapSize.set(1_024, 1_024);
  sun.shadow.camera.left = -135;
  sun.shadow.camera.right = 135;
  sun.shadow.camera.top = 135;
  sun.shadow.camera.bottom = -135;
  sun.shadow.camera.near = 25;
  sun.shadow.camera.far = 360;
  sun.shadow.bias = -0.0008;
  scene.add(hemisphere, sun);

  return {
    update(x: number, z: number, delta = 0, snap = false): void {
      const target = atmosphereTarget(biomeWeightsAt(x, z));
      const amount = snap ? 1 : 1 - Math.exp(-Math.max(delta, 0) * 0.72);
      background.lerp(target.sky, amount);
      fog.color.lerp(target.fog, amount);
      sun.color.lerp(target.sun, amount);
      hemisphere.color.lerp(target.hemisphereSky, amount);
      hemisphere.groundColor.lerp(target.hemisphereGround, amount);
      fog.near += (target.fogNear - fog.near) * amount;
      fog.far += (target.fogFar - fog.far) * amount;
    },
    dispose(): void {
      scene.remove(hemisphere, sun);
      sun.dispose();
      hemisphere.dispose();
    },
  };
}
